// Top center: team scores / leader, match clock, phase label, alive pips, and the objective line below.

import { div, setClass, setText, span } from "./dom";
import { countTeamAlive, damageArcRotationDeg, isClockRunning, phaseLabel, quantize, teamName, timerText } from "./format";
import { icon } from "./icons";
import type { MatchHudView, ObjectiveHudView, ScoreboardRow, TeamTag } from "./types";

const MAX_PIPS = 8;

class ScoreBox {
  readonly root = div("hud-score");
  private readonly labelEl = span("hud-score__label");
  private readonly valEl = span("hud-score__val");
  private team: TeamTag | "" = "";
  private label = "";
  private val = "";
  private mine = false;

  constructor() {
    this.root.append(this.labelEl, this.valEl);
  }

  set(team: TeamTag, label: string, val: string, mine: boolean, isName = false): void {
    if (team !== this.team) {
      this.team = team;
      this.root.dataset.team = team;
    }
    if (label !== this.label) {
      this.label = label;
      this.labelEl.textContent = label;
    }
    if (val !== this.val) {
      this.val = val;
      this.valEl.textContent = val;
    }
    setClass(this.root, "is-mine", mine);
    setClass(this.valEl, "is-name", isName);
  }
}

class Pips {
  readonly root = div("hud-pips");
  private readonly items: HTMLSpanElement[] = [];
  private alive = -1;
  private total = -1;

  constructor() {
    for (let i = 0; i < MAX_PIPS; i++) {
      const p = span("hud-pip");
      p.hidden = true;
      this.items.push(p);
      this.root.append(p);
    }
  }

  set(alive: number, total: number): void {
    if (alive === this.alive && total === this.total) return;
    this.alive = alive;
    this.total = total;
    for (let i = 0; i < MAX_PIPS; i++) {
      const p = this.items[i]!;
      p.hidden = i >= total;
      setClass(p, "is-alive", i < alive);
    }
  }
}

export class MatchBar {
  readonly root = div("hud-matchbar");
  private readonly left = new ScoreBox();
  private readonly right = new ScoreBox();
  private readonly timerEl = div("hud-match__timer");
  private readonly phaseEl = div("hud-match__phase");
  private readonly pipsL = new Pips();
  private readonly pipsR = new Pips();
  private readonly pipsBox = div("hud-match__pips");
  private readonly alive = new Int32Array(4);

  private m: MatchHudView | null = null;
  private baseSec: number | null = null;
  private setAtMs = 0;
  private lastSec = -2;

  constructor() {
    const row = div("hud-match");
    const mid = div("hud-match__mid");
    mid.append(this.timerEl, this.phaseEl);
    row.append(this.left.root, mid, this.right.root);
    this.pipsBox.append(this.pipsL.root, this.pipsR.root);
    this.root.append(row, this.pipsBox);
    this.root.hidden = true;
  }

  set(m: MatchHudView, nowMs: number): void {
    this.m = m;
    this.baseSec = m.timeLeftSec;
    this.setAtMs = nowMs;
    this.root.hidden = false;
    this.root.dataset.phase = m.phase;
    this.renderBoxes(m);
    this.renderClock(m.timeLeftSec);
    this.renderPips();
  }

  /** Alive counts per team (Defusal) come from the scoreboard rows. */
  setRows(rows: readonly ScoreboardRow[]): void {
    countTeamAlive(rows, this.alive);
    this.renderPips();
  }

  /** Per frame: keep the clock running between `setMatch` calls (text changes once a second). */
  update(nowMs: number): void {
    const m = this.m;
    if (!m || this.baseSec === null || !isClockRunning(m.phase)) return;
    const sec = Math.max(0, Math.ceil(this.baseSec - (nowMs - this.setAtMs) / 1000));
    if (sec !== this.lastSec) this.renderClock(sec);
  }

  private renderClock(sec: number | null): void {
    const m = this.m;
    if (!m) return;
    this.lastSec = sec === null ? -1 : Math.ceil(sec);
    setText(this.timerEl, timerText(m, sec));
    setText(this.phaseEl, phaseLabel(m, sec));
    this.timerEl.hidden = this.timerEl.textContent === "";
  }

  private renderBoxes(m: MatchHudView): void {
    if (m.mode === "ffa") {
      this.left.set("none", "YOU", m.myScore === null ? "\u2013" : String(m.myScore), true);
      if (m.leaderName === null) this.right.root.hidden = true;
      else {
        this.right.root.hidden = false;
        this.right.set("none", "LEADER", m.leaderName, false, true);
      }
      return;
    }
    this.right.root.hidden = false;
    const mine: "a" | "b" = m.myTeam === "b" ? "b" : "a";
    const other: "a" | "b" = mine === "a" ? "b" : "a";
    const own = m.myTeam !== "none";
    const sc = m.scores;
    // Defusal: team A is always the attacking side (the teams swap sides at half time).
    const name = (t: "a" | "b"): string => (m.mode === "defusal" ? (t === "a" ? "ATTACK" : "DEFEND") : teamName(t));
    this.left.set(mine, name(mine), sc ? String(sc[mine === "a" ? 0 : 1]) : "\u2013", own);
    this.right.set(other, name(other), sc ? String(sc[other === "a" ? 0 : 1]) : "\u2013", false);
  }

  private renderPips(): void {
    const m = this.m;
    if (m === null || m.mode !== "defusal") {
      this.pipsBox.hidden = true;
      return;
    }
    this.pipsBox.hidden = false;
    const mineIsA = m.myTeam !== "b";
    const a = this.alive;
    this.pipsL.set(mineIsA ? a[0]! : a[2]!, mineIsA ? a[1]! : a[3]!);
    this.pipsR.set(mineIsA ? a[2]! : a[0]!, mineIsA ? a[3]! : a[1]!);
    this.pipsL.root.dataset.team = mineIsA ? "a" : "b";
    this.pipsR.root.dataset.team = mineIsA ? "b" : "a";
  }
}

export class ObjectiveLine {
  readonly root = div("hud-obj");
  private readonly arrow = icon("arrow", "hud-obj__arrow");
  private readonly labelEl = span("hud-obj__label");
  private readonly distEl = span("hud-obj__dist");
  private readonly subEl = span("hud-obj__sub");
  private readonly barFill = span("hud-obj__fill");
  private readonly bar = div("hud-obj__bar");

  private label = "";
  private sub: string | null = null;
  private tone = "";
  private kind = "";
  private deg = NaN;
  private hasArrow = true;
  private dist = -1;
  private prog = -2;

  constructor() {
    this.bar.append(this.barFill);
    const row = div("hud-obj__row");
    row.append(this.arrow, this.labelEl, this.distEl);
    this.subEl.hidden = true;
    this.root.append(row, this.subEl, this.bar);
    this.root.hidden = true;
  }

  /** Called every frame by the session (the arrow follows the view): only changes touch the DOM. */
  set(o: ObjectiveHudView | null): void {
    if (o === null) {
      this.root.hidden = true;
      return;
    }
    this.root.hidden = false;
    if (o.kind !== this.kind) {
      this.kind = o.kind;
      this.root.dataset.kind = o.kind;
    }
    if (o.tone !== this.tone) {
      this.tone = o.tone;
      this.root.dataset.tone = o.tone;
    }
    if (o.label !== this.label) {
      this.label = o.label;
      this.labelEl.textContent = o.label;
    }
    if (o.sub !== this.sub) {
      this.sub = o.sub;
      this.subEl.hidden = o.sub === null;
      this.subEl.textContent = o.sub ?? "";
    }
    const hasArrow = o.dirRad !== null;
    if (hasArrow !== this.hasArrow) {
      this.hasArrow = hasArrow;
      this.arrow.style.display = hasArrow ? "" : "none";
    }
    if (o.dirRad !== null) {
      const deg = Math.round(damageArcRotationDeg(o.dirRad));
      if (deg !== this.deg) {
        this.deg = deg;
        this.arrow.style.transform = `rotate(${deg}deg)`;
      }
    }
    const dist = o.distanceM === null ? -1 : Math.round(o.distanceM);
    if (dist !== this.dist) {
      this.dist = dist;
      this.distEl.textContent = dist < 0 ? "" : `${dist} m`;
    }
    const prog = o.progress === null ? -1 : quantize(Math.min(1, Math.max(0, o.progress)), 0.01);
    if (prog !== this.prog) {
      this.prog = prog;
      this.bar.hidden = prog < 0;
      if (prog >= 0) this.barFill.style.transform = `scaleX(${prog})`;
    }
  }
}
